import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
  SmartToy as KamIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'analyses', label: 'Analyses' },
  { id: 'ideas', label: 'Ideas' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'research', label: 'Research' },
  { id: 'notes', label: 'Notes' }
];

const JournalPage = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bimesterFilter, setBimesterFilter] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tokenLimit, setTokenLimit] = useState(100);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [brandName, setBrandName] = useState('');

  // Load journal entries
  useEffect(() => {
    const loadEntries = async () => {
      if (!brandId) return;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeTab !== 'all') params.append('category', activeTab);
        if (bimesterFilter) params.append('bimester', bimesterFilter);
        if (searchQuery) params.append('search', searchQuery);

        const response = await axios.get(`/api/journal/${brandId}?${params}`);
        if (response.data?.success) {
          setEntries(response.data.entries);
        }
      } catch (error) {
        console.error('Error loading journal:', error);
        toast.error('Failed to load journal entries');
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, [brandId, activeTab, bimesterFilter, searchQuery]);

  // Load brand name
  useEffect(() => {
    const loadBrand = async () => {
      if (!brandId) return;
      try {
        const response = await axios.get(`/api/brands/${brandId}`);
        if (response.data?.brand) {
          setBrandName(response.data.brand.name || 'Unknown Brand');
        }
      } catch (error) {
        console.error('Error loading brand:', error);
      }
    };
    loadBrand();
  }, [brandId]);

  // Toggle pin
  const handleTogglePin = async (entryId) => {
    try {
      const response = await axios.put(`/api/journal/${entryId}/pin`);
      if (response.data?.success) {
        setEntries(entries.map(e =>
          e._id === entryId ? { ...e, isPinned: response.data.entry.isPinned } : e
        ));
      }
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  // Archive entry
  const handleArchive = async (entryId) => {
    try {
      const response = await axios.put(`/api/journal/${entryId}/archive`);
      if (response.data?.success) {
        setEntries(entries.filter(e => e._id !== entryId));
        toast.success('Entry archived');
      }
    } catch (error) {
      toast.error('Failed to archive entry');
    }
  };

  // Delete entry
  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      const response = await axios.delete(`/api/journal/${entryId}`);
      if (response.data?.success) {
        setEntries(entries.filter(e => e._id !== entryId));
        toast.success('Entry deleted');
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  // Save user note
  const handleSaveNote = async () => {
    if (!editingEntry) return;

    try {
      const response = await axios.put(`/api/journal/${editingEntry}`, {
        userNote: editNoteText
      });
      if (response.data?.success) {
        setEntries(entries.map(e =>
          e._id === editingEntry ? { ...e, userNote: editNoteText } : e
        ));
        setEditingEntry(null);
        setEditNoteText('');
        toast.success('Note saved');
      }
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  // Get unique bimesters for filter
  const bimesters = [...new Set(entries.map(e => e.context?.bimester).filter(Boolean))];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0f1a', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#111928', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: '#fff' }}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#fff">
                Design Journal
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.7)">
                {brandName}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setSettingsOpen(true)} sx={{ color: '#fff' }}>
            <SettingsIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* Tabs & Filters */}
      <Paper sx={{ mb: 3, bgcolor: '#111928', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)' },
            '& .Mui-selected': { color: '#00d9ff' },
            '& .MuiTabs-indicator': { bgcolor: '#00d9ff' }
          }}
        >
          {TABS.map(tab => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>

        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  bgcolor: '#0d1320',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: '#3b82f6' }
                }
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Bimester</InputLabel>
              <Select
                value={bimesterFilter}
                onChange={(e) => setBimesterFilter(e.target.value)}
                label="Bimester"
                sx={{
                  color: '#fff',
                  bgcolor: '#0d1320',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: '#3b82f6' }
                }}
              >
                <MenuItem value="">All</MenuItem>
                {bimesters.map(b => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Paper>

      {/* Entries */}
      {loading ? (
        <Typography color="rgba(255,255,255,0.7)" textAlign="center" py={4}>
          Loading journal...
        </Typography>
      ) : entries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#111928', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography color="rgba(255,255,255,0.7)">
            No journal entries yet. Save Kam's responses to build your design journal.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {entries.map(entry => (
            <Card key={entry._id} sx={{
              borderLeft: entry.isPinned ? '4px solid #3b82f6' : 'none',
              bgcolor: '#111928',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <CardContent>
                {/* Entry Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {entry.isPinned && <PinIcon fontSize="small" sx={{ color: '#3b82f6' }} />}
                      <Typography variant="subtitle1" fontWeight={600} color="#fff">
                        {entry.title || entry.type || 'Journal Entry'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={0.5}>
                      <Chip
                        label={entry.category}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)' }}
                      />
                      {entry.context?.bimester && (
                        <Chip
                          label={entry.context.bimester}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#00d9ff', color: '#00d9ff' }}
                        />
                      )}
                      <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ alignSelf: 'center' }}>
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row">
                    <Tooltip title={entry.isPinned ? 'Unpin' : 'Pin'}>
                      <IconButton size="small" onClick={() => handleTogglePin(entry._id)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {entry.isPinned ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive">
                      <IconButton size="small" onClick={() => handleArchive(entry._id)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(entry._id)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Question if exists */}
                {entry.question && (
                  <Typography variant="body2" color="rgba(255,255,255,0.5)" sx={{ mb: 1, fontStyle: 'italic' }}>
                    Q: {entry.question}
                  </Typography>
                )}

                {/* Response */}
                <Box sx={{
                  bgcolor: '#0d1320',
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  '& h1, & h2, & h3': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5, color: '#fff' },
                  '& p': { fontSize: '0.875rem', my: 0.5, color: 'rgba(255,255,255,0.85)' },
                  '& ul, & ol': { pl: 2, my: 0.5, color: 'rgba(255,255,255,0.85)' },
                  '& li': { fontSize: '0.875rem', mb: 0.25 },
                  '& strong': { color: '#fff' }
                }}>
                  <ReactMarkdown>{entry.response}</ReactMarkdown>
                </Box>

                {/* Kam's Note */}
                {entry.kamNote && (
                  <Box sx={{
                    bgcolor: 'rgba(0, 217, 255, 0.1)',
                    p: 2,
                    borderRadius: 1,
                    mb: 2,
                    borderLeft: '3px solid #00d9ff'
                  }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <KamIcon fontSize="small" sx={{ color: '#00d9ff' }} />
                      <Typography variant="subtitle2" sx={{ color: '#00d9ff' }} fontWeight={600}>
                        Kam's Note
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'rgba(255,255,255,0.85)' }}>
                      {entry.kamNote}
                    </Typography>
                  </Box>
                )}

                {/* User's Note */}
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', pt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
                      Your Notes
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingEntry(entry._id);
                        setEditNoteText(entry.userNote || '');
                      }}
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {entry.userNote ? (
                    <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.85)' }}>
                      {entry.userNote}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="rgba(255,255,255,0.5)" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Click edit to add your notes...
                    </Typography>
                  )}
                </Box>

                {/* Tags */}
                {entry.tags?.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap">
                    {entry.tags.map(tag => (
                      <Chip key={tag} label={tag} size="small" sx={{ mb: 0.5, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Edit Note Dialog */}
      <Dialog
        open={!!editingEntry}
        onClose={() => setEditingEntry(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#111928', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Edit Your Note</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
            placeholder="Add your personal notes..."
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                bgcolor: '#0d1320',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: '#3b82f6' }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingEntry(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Cancel</Button>
          <Button onClick={handleSaveNote} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#111928', border: '1px solid rgba(255,255,255,0.1)' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Journal Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom color="#fff">
              Kam's Note Length: {tokenLimit} tokens
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.5)" gutterBottom>
              Controls how detailed Kam's auto-generated notes are
            </Typography>
            <Slider
              value={tokenLimit}
              onChange={(e, v) => setTokenLimit(v)}
              min={50}
              max={300}
              step={25}
              marks={[
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: 200, label: '200' },
                { value: 300, label: '300' }
              ]}
              sx={{
                mt: 2,
                color: '#00d9ff',
                '& .MuiSlider-markLabel': { color: 'rgba(255,255,255,0.5)' }
              }}
            />
            <Typography variant="body2" color="rgba(255,255,255,0.5)" sx={{ mt: 2 }}>
              Preview: {tokenLimit <= 75 ? '1-2 sentences' : tokenLimit <= 150 ? '2-3 sentences' : '3-4 sentences'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JournalPage;
